import ValibotCommon from '@shared-schemas/common'
import * as v from 'valibot'
import {
	DEFAULT_OPERATING_MODE,
	OPERATING_MODES,
	tms_sequence_data,
} from '@shared-schemas/optimus/optimus-config-v2'

const legacy_sequence_shape = v.object({
	ring_1: v.optional(v.array(v.number())),
	ring_2: v.optional(v.array(v.number())),
})
const optimus_sequence_shape = v.union([tms_sequence_data, legacy_sequence_shape])

export const getIsOptimusEnabledSchema = () => {
	return v.object({
		enabled: v.boolean(),
	})
}

type GenerateSchemaParams = Pick<
	Parameters<typeof ValibotCommon.getUuidSchema_V2>[0],
	'expected_format' | 'convert_format'
>

// ====================================
// =========== Shared Picklists =======
// ====================================

export const job_status_schema = v.picklist([
	'queued',
	'running',
	'failed',
	'in_review',
	'approved',
	'deployed',
	'rejected',
	'deleted',
	'completed',
])

export const optimus_tod_days_schema = v.array(
	v.picklist(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']),
)

export const optimus_tod_mode_schema = v.picklist(['coordinated'])

export const cardinal_direction_schema = v.picklist(['NB', 'SB', 'EB', 'WB'])

// ====================================
// =========== Shared Sub-Schemas =====
// ====================================

const optimus_tms_phase_schema = v.object({
	red: v.optional(v.nullable(v.number())),
	ring: v.nullable(v.number()),
	green: v.nullable(v.number()),
	split: v.nullable(v.number()),
	yellow: v.nullable(v.number()),
	barrier: v.nullable(v.number()),
	minimum_green: v.nullable(v.number()),
	is_coordinated: v.nullable(v.boolean()),
	sequence_index: v.nullable(v.number()),
	edge_connection_mapping: v.optional(
		v.optional(
			v.nullable(
				v.array(v.tuple([v.optional(v.string()), v.optional(v.string()), v.optional(v.nullable(v.string()))])),
			),
		),
	),
})

const optimus_phase_data_entry_schema = v.partial(
	v.object({
		green: v.nullable(v.number()),
		split: v.nullable(v.number()),
		weight: v.nullable(v.number()),
		yellow: v.nullable(v.number()),
		phase_id: v.nullable(v.string()),
		movement_ids: v.nullable(v.array(v.string())),
		arrival_histogram: v.optional(v.nullable(v.array(v.number()))),
		queue_length_meters: v.nullable(v.number()),
		travel_time_seconds: v.nullable(v.number()),
		control_delay_seconds: v.nullable(v.number()),
		approach_delay_seconds: v.nullable(v.number()),
		red_percentage_histogram: v.nullable(v.array(v.number())),
		green_percentage_histogram: v.nullable(v.array(v.number())),
		yellow_percentage_histogram: v.nullable(v.array(v.number())),
		arrivals_on_green_percentage: v.nullable(v.number()),
		advanced_to_stopbar_time_seconds: v.nullable(v.number()),
		red_percentage_histogram_theoretical: v.nullable(v.array(v.number())),
		green_percentage_histogram_theoretical: v.nullable(v.array(v.number())),
		yellow_percentage_histogram_theoretical: v.nullable(v.array(v.number())),
		abacus_volume_per_hour: v.optional(v.nullable(v.number())),
		effective_green_time: v.optional(v.nullable(v.number())),
	}),
)

const optimus_network_metrics_schema = v.object({
	network_queue_length_meters: v.nullable(v.number()),
	network_control_delay_seconds: v.nullable(v.number()),
	network_arrival_on_green_percentage: v.nullable(v.number()),
	// Optional Metrics
	network_abacus_volume_per_hour: v.optional(v.number()),
	abacus_volume_per_hour: v.optional(v.number()),
	effective_green_time: v.optional(v.number()),
	network_effective_green_time: v.optional(v.number()),
})

const optimus_offset_constraints_schema = v.object({
	sequence: v.optional(v.nullable(optimus_sequence_shape)),
	force_off_mode: v.optional(v.nullable(v.string()), null),
	enable_optimization: v.boolean(),
	offset_reference_point: v.optional(v.nullable(v.string())),
	current_offset: v.optional(v.nullable(v.number()), null),
})

const optimus_edge_connection_mapping_schema = v.object({
	id: v.string(),
	error: v.nullable(v.string()),
	phases: v.array(
		v.object({
			key: v.string(),
			number: v.number(),
			atomicMovements: v.array(
				v.object({
					id: v.string(),
					key: v.string(),
					priority: v.string(),
					movementType: v.nullable(v.string()),
					edgeConnectionId: v.string(),
				}),
			),
		}),
	),
	status: v.string(),
	valid_to: v.nullable(v.string()),
	valid_from: v.string(),
	phasePlanId: v.string(),
	valid_range: v.optional(v.string()),
	atlasSignalId: v.string(),
	phase_settings_external_id: v.optional(v.string()),
})

// current_mode is the deployed operating mode (the baseline projected into signals_deployed); mode is
// the requested target. The pair expresses the transition, e.g. coordinated -> fixed-time.
const optimus_operating_mode_constraints_schema = v.object({
	current_mode: v.optional(v.picklist(OPERATING_MODES), DEFAULT_OPERATING_MODE),
	mode: v.optional(v.picklist(OPERATING_MODES), DEFAULT_OPERATING_MODE),
})

const optimus_cycle_length_constraints_schema = v.object({
	max_cycle_length: v.optional(v.nullable(v.number())),
	min_cycle_length: v.optional(v.nullable(v.number())),
	enable_optimization: v.boolean(),
	current_cycle_length: v.optional(v.nullable(v.number())),
})

const optimus_phase_settings_schema = v.object({
	enabled: v.boolean(),
	maxSplit: v.nullable(v.number()),
	minSplit: v.number(),
	vehicleExt: v.number(),
	coordinated: v.boolean(),
	minimumGreen: v.number(),
	redClearance: v.number(),
	allowLeftTurn: v.boolean(),
	minPedWalkTime: v.optional(v.nullable(v.number())),
	minPedClearance: v.optional(v.nullable(v.number())),
	yellowClearance: v.number(),
	dummyPhase: v.optional(v.boolean()),
	pedestrianPhase: v.optional(v.boolean()),
})

const optimus_split_constraint_schema = v.object({
	number: v.number(),
	max_split: v.optional(v.nullable(v.number())),
	min_split: v.optional(v.nullable(v.number())),
	is_enabled: v.boolean(),
	is_coordinated: v.boolean(),
	current_split: v.optional(v.nullable(v.number())),
	yellow: v.optional(v.nullable(v.number())),
	red: v.optional(v.nullable(v.number())),
	minimum_green: v.optional(v.nullable(v.number())),
	min_pedestrian_walk_time: v.optional(v.nullable(v.number())),
	min_pedestrian_clearance: v.optional(v.nullable(v.number())),
})

const optimus_ml_split_constraint_schema = v.object({
	...optimus_tms_phase_schema.entries,
	number: v.number(),
	max_split: v.nullable(v.number()),
	min_split: v.nullable(v.number()),
	is_enabled: v.boolean(),
	current_split: v.nullable(v.number()),
	vehicle_extension: v.optional(v.number(), 3),
	min_pedestrian_walk_time: v.optional(v.nullable(v.number())),
	min_pedestrian_clearance: v.optional(v.nullable(v.number())),
	dummy_phase: v.optional(v.boolean()),
	pedestrian_phase: v.optional(v.boolean()),
})

// Per-phase shape for the deployed (current) signal config. Mirrors the deployed subset of
// optimus_ml_split_constraint_schema — constraint-only fields (min/max split, minimum_green,
// pedestrian mins, green/split) are intentionally omitted.
const optimus_deployed_split_schema = v.object({
	red: v.nullable(v.number()),
	ring: v.nullable(v.number()),
	number: v.number(),
	yellow: v.nullable(v.number()),
	barrier: v.nullable(v.number()),
	is_enabled: v.boolean(),
	dummy_phase: v.optional(v.boolean()),
	current_split: v.nullable(v.number()),
	vehicle_extension: v.optional(v.number(), 3),
	minimum_green: v.optional(v.nullable(v.number())),
	is_coordinated: v.nullable(v.boolean()),
	sequence_index: v.nullable(v.number()),
	pedestrian_phase: v.optional(v.boolean()),
	edge_connection_mapping: v.optional(
		v.nullable(
			v.array(v.tuple([v.optional(v.string()), v.optional(v.string()), v.optional(v.nullable(v.string()))])),
		),
	),
})

// Per-parameter verdict from the deployed-config resolver. `inferred` means no single programmed
// value held the job window, so the matching deployed field is null and the optimizer generates its
// own t=0 baseline — the UI marks any comparison against it as an estimate.
const optimus_deployed_param_resolution_schema = v.object({
	status: v.picklist(['programmed', 'inferred']),
	coverage_pct: v.number(),
	uncovered_minutes_per_day: v.number(),
	plan_numbers: v.array(v.string()),
})

// Query for the pre-submit deployed-resolution check: the same job window the modal is about to
// submit, so the asterisk it renders matches the verdict the submit path will persist.
export const getDeployedResolutionQuerySchema = () => {
	return v.object({
		local_start_time: v.string(),
		local_end_time: v.string(),
		days: v.string(),
		date_start: v.string(),
		date_end: v.string(),
		excluded_dates: v.optional(v.string()),
	})
}

export const optimus_deployed_resolution_schema = v.object({
	cycle: optimus_deployed_param_resolution_schema,
	offset: optimus_deployed_param_resolution_schema,
	splits: optimus_deployed_param_resolution_schema,
	sequence: optimus_deployed_param_resolution_schema,
	mode: optimus_deployed_param_resolution_schema,
	phase_settings: optimus_deployed_param_resolution_schema,
})

// Every distinct value a parameter actually ran during the job window, with the plans and the local
// times behind each. Recorded for every signal, so a parameter that held one value all window has a
// single candidate at 100% — which is what lets the report read its "before" from here uniformly
// instead of from split_input. `value` is absent for phase_settings, whose blob is too large to be
// worth persisting per candidate.
const optimus_overlapping_plan_window_schema = v.object({
	days: v.array(v.string()),
	local_start_time: v.string(),
	local_end_time: v.string(),
})

const optimus_overlapping_plan_candidate_schema = v.object({
	plan_numbers: v.array(v.string()),
	value: v.optional(v.unknown()),
	coverage_pct: v.number(),
	// Absent on jobs submitted before the split was recorded; callers fall back to coverage_pct alone.
	range_pct: v.optional(v.number()),
	tod_pct: v.optional(v.number()),
	minutes_per_day: v.number(),
	windows: v.array(optimus_overlapping_plan_window_schema),
})

// Candidates are capped, so omitted_count / omitted_coverage_pct say what was dropped rather than
// letting a truncated list read as complete.
const optimus_overlapping_plan_group_schema = v.object({
	candidates: v.array(optimus_overlapping_plan_candidate_schema),
	omitted_count: v.number(),
	omitted_coverage_pct: v.number(),
})

export const optimus_overlapping_plans_schema = v.object({
	cycle: optimus_overlapping_plan_group_schema,
	offset: optimus_overlapping_plan_group_schema,
	splits: optimus_overlapping_plan_group_schema,
	sequence: optimus_overlapping_plan_group_schema,
	mode: optimus_overlapping_plan_group_schema,
	phase_settings: optimus_overlapping_plan_group_schema,
})

// ====================================
// =========== TOD ====================
// ====================================

export const getOptimusV2TodSchema = (params: GenerateSchemaParams) => {
	return v.object({
		id: v.union([
			v.literal('custom'),
			ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'tod_settings.id' }),
		]),
		days: optimus_tod_days_schema,
		mode: v.optional(
			v.union([
				v.pipe(
					v.literal(''),
					v.transform(() => null),
				),
				v.null_(),
				optimus_tod_mode_schema,
			]),
		),
		name: v.string(),
		days_boolean: v.object({
			has_friday: v.boolean(),
			has_monday: v.boolean(),
			has_sunday: v.boolean(),
			has_tuesday: v.boolean(),
			has_saturday: v.boolean(),
			has_thursday: v.boolean(),
			has_wednesday: v.boolean(),
		}),
		local_end_time: v.string(),
		local_start_time: v.string(),
		months: v.optional(
			v.object({
				january: v.boolean(),
				february: v.boolean(),
				march: v.boolean(),
				april: v.boolean(),
				may: v.boolean(),
				june: v.boolean(),
				july: v.boolean(),
				august: v.boolean(),
				september: v.boolean(),
				october: v.boolean(),
				november: v.boolean(),
				december: v.boolean(),
			}),
		),
		days_of_month: v.optional(v.record(v.string(), v.boolean())),
	})
}

// ====================================
// =========== Job List ===============
// ====================================

export const getOptimusV2JobSchema = (params: GenerateSchemaParams) => {
	return v.object({
		job_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'job_id' }),
		parent_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'parent_id' }),
		created_at: ValibotCommon.getDateOrIsoStringSchema(),
		local_created_at: ValibotCommon.getDateOrIsoStringSchema(),
		updated_at: ValibotCommon.getDateOrIsoStringSchema(),
		local_updated_at: ValibotCommon.getDateOrIsoStringSchema(),
		deployment_date: v.nullable(ValibotCommon.getDateOrIsoStringSchema()),
		local_deployment_date: v.nullable(ValibotCommon.getDateOrIsoStringSchema()),
		deployed_by: v.nullable(ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'deployed_by' })),
		customer_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'customer_id' }),
		requested_by: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'requested_by' }),
		status: job_status_schema,
		approved_by: v.union([v.null_(), ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'approved_by' })]),
		requested_by_name: v.string(),
		approved_by_name: v.nullable(v.string()),
		tod_name: v.string(),
		tod_id: v.union([v.literal('custom'), ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'tod_id' })]),
		tod_settings: getOptimusV2TodSchema(params),
		job_name: v.nullable(v.string()),
		network_id: v.optional(v.string()),
		network_name: v.string(),
		version: v.pipe(v.number(), v.finite()),
		tod_display: v.string(),
		is_visible: v.optional(v.boolean()),
		is_remote: v.optional(v.boolean()),
	})
}

export const getOptimusUpdateJobNameSchema = (params: GenerateSchemaParams) => {
	return v.object({
		job_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'job_id' }),
		job_name: v.string(),
	})
}

export const getOptimusUpdateJobFlagsSchema = (params: GenerateSchemaParams) => {
	return v.object({
		job_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'job_id' }),
		is_visible: v.boolean(),
		is_remote: v.boolean(),
	})
}

// ====================================
// =========== Offset / Signal ========
// ====================================

export const getOptimusV2SignalSchema = (params: GenerateSchemaParams) => {
	return v.object({
		colors: v.optional(
			v.object({
				red: v.array(v.tuple([v.number(), v.number()])),
				green: v.array(v.tuple([v.number(), v.number()])),
			}),
		),
		phase_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'signal.phase_id' }),
		signal_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'signal.signal_id' }),
		phase_name: v.string(),
		plan_cycle: v.optional(v.nullable(v.number())),
		movement_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'signal.movement_id' }),
		plan_offset: v.optional(v.nullable(v.number())),
		signal_name: v.string(),
		name_short: v.optional(v.string()),
		phase_number: v.number(),
		upstream_phase: v.nullable(v.number()),
		distance_meters: v.number(),
		intersection_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'signal.intersection_id' }),
		downstream_phase: v.nullable(v.number()),
		offset_available: v.optional(v.boolean()),
		sequential_index: v.number(),
		atomic_movement_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'signal.atomic_movement_id' }),
		edge_connection_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'signal.edge_connection_id' }),
		upstream_signal_id: v.nullable(
			ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'signal.upstream_signal_id' }),
		),
		travel_time_seconds: v.number(),
		downstream_signal_id: v.nullable(
			ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'signal.downstream_signal_id' }),
		),
		upstream_movement_id: v.nullable(
			ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'signal.upstream_movement_id' }),
		),
		bandwidths_coordinates: v.optional(v.array(v.array(v.tuple([v.number(), v.number()])))),
		downstream_movement_id: v.nullable(
			ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'signal.downstream_movement_id' }),
		),
		non_signalized_direction: v.boolean(),
		arrival_on_green_percentage: v.optional(v.nullable(v.number())),
		upstream_edge_connection_id: v.nullable(
			ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'signal.upstream_edge_connection_id' }),
		),
		distance_meters_to_signalized: v.number(),
		downstream_edge_connection_id: v.nullable(
			ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'signal.downstream_edge_connection_id' }),
		),
		bandwidths_mean_duration_seconds: v.optional(v.nullable(v.number())),
		travel_time_seconds_to_signalized: v.number(),
		upstream_signalized_intersection_id: v.nullable(
			ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'signal.upstream_signalized_intersection_id' }),
		),
		downstream_signalized_intersection_id: v.nullable(
			ValibotCommon.getUuidSchema_V2({
				...params,
				error_field_name: 'signal.downstream_signalized_intersection_id',
			}),
		),
	})
}

export const getOptimusV2OffsetInputSchema = (params: GenerateSchemaParams) => {
	return v.object({
		weight: v.optional(v.nullable(v.number())),
		signals: v.array(getOptimusV2SignalSchema(params)),
		route_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'offset_input.route_id' }),
		direction: cardinal_direction_schema,
		safety_weight: v.number(),
		bandwidth_weight: v.number(),
		directional_bias: v.number(),
		arrival_on_green_percentage: v.optional(v.number()),
		network_arrival_on_green_percentage: v.optional(v.number()),
		bandwidths_mean_total_duration_seconds: v.optional(v.number()),
		network_bandwidths_mean_total_duration_seconds: v.optional(v.number()),
	})
}

// ====================================
// =========== Split Signal ===========
// ====================================

const getOptimusSplitSignalSchema = () => {
	return v.object({
		cycle: v.optional(v.nullable(v.number())),
		offset: v.optional(v.nullable(v.number())),
		volume: v.optional(v.nullable(v.number())),
		tms_data: v.optional(v.record(v.string(), optimus_tms_phase_schema)),
		phase_data: v.optional(v.record(v.string(), optimus_phase_data_entry_schema)),
		plan_offset: v.optional(v.nullable(v.number())),
		signal_name: v.optional(v.string()),
		name_short: v.optional(v.string()),
		total_weight: v.optional(v.nullable(v.number())),
		intersection_ids: v.optional(v.array(v.string())),
		sequential_index: v.optional(v.number()),
		mean_queue_length_meters: v.optional(v.nullable(v.number())),
		total_queue_length_meters: v.optional(v.nullable(v.number())),
		mean_control_delay_seconds: v.optional(v.nullable(v.number())),
		total_control_delay_seconds: v.optional(v.nullable(v.number())),
		// Optional
		arrivals_on_green_percentage: v.optional(v.nullable(v.number())),
		abacus_volume_per_hour: v.optional(v.nullable(v.number())),
	})
}

// ====================================
// =========== Settings Signals =======
// ====================================

const getOptimusSettingsSignalSchema = (params: GenerateSchemaParams) => {
	return v.object({
		id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'settings.signal.id' }),
		is_configured: v.optional(v.boolean()),
		phase_settings: v.optional(v.record(v.string(), optimus_phase_settings_schema)),
		intersection_ids: v.optional(v.array(v.string())),
		split_constraints: v.optional(v.array(optimus_split_constraint_schema)),
		offset_constraints: v.optional(optimus_offset_constraints_schema),
		operating_mode_constraints: v.optional(optimus_operating_mode_constraints_schema),
		enable_optimization: v.boolean(),
		edge_connection_mapping: v.optional(optimus_edge_connection_mapping_schema),
		cycle_length_constraints: v.optional(optimus_cycle_length_constraints_schema),
		enable_split_optimization: v.optional(v.boolean()),
		has_flowlabs_supplemental_params: v.optional(v.boolean()),
		sequence_constraints: v.optional(
			v.object({
				sequence: v.nullable(optimus_sequence_shape),
			}),
		),
	})
}

const getOptimusSettingsSignalOptimizationSchema = (params: GenerateSchemaParams) => {
	return v.object({
		id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'settings.signals_optimization.id' }),
		name: v.optional(v.nullable(v.string())),
		is_configured: v.optional(v.boolean()),
		intersection_ids: v.optional(v.array(v.string())),
		split_constraints: v.record(v.string(), optimus_ml_split_constraint_schema),
		offset_constraints: v.optional(optimus_offset_constraints_schema),
		operating_mode_constraints: v.optional(optimus_operating_mode_constraints_schema),
		enable_optimization: v.boolean(),
		edge_connection_mapping: v.optional(optimus_edge_connection_mapping_schema),
		cycle_length_constraints: optimus_cycle_length_constraints_schema,
		enable_split_optimization: v.optional(v.boolean()),
		has_flowlabs_supplemental_params: v.optional(v.boolean()),
	})
}

const getOptimusSettingsSignalDeployedSchema = (params: GenerateSchemaParams) => {
	return v.object({
		id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'settings.signals_deployed.id' }),
		name: v.optional(v.nullable(v.string())),
		is_configured: v.optional(v.boolean()),
		intersection_ids: v.optional(v.array(v.string())),
		splits: v.record(v.string(), optimus_deployed_split_schema),
		sequence: v.nullable(optimus_sequence_shape),
		current_offset: v.nullable(v.number()),
		force_off_mode: v.nullable(v.string()),
		offset_reference_point: v.nullable(v.string()),
		cycle: v.nullable(v.number()),
		mode: v.optional(v.picklist(OPERATING_MODES), DEFAULT_OPERATING_MODE),
		is_missing_params: v.boolean(),
		// Optional so jobs submitted before deployed-config resolution existed still validate on read.
		deployed_resolution: v.optional(optimus_deployed_resolution_schema),
		// Phases whose programmed split cannot fit min green + yellow + red. The split is kept as-is;
		// this flags the controller config as broken rather than hiding an impossible baseline.
		violating_phases: v.optional(v.array(v.number())),
	})
}

export const getOptimusJobSettingsSchema = (params: GenerateSchemaParams) => {
	return v.object({
		tod: getOptimusV2TodSchema(params),
		// Legacy fields, retained as optional so historical jobs still validate. `signals_optimization`
		// is the canonical field; `signals_ml`/`signals` are read-fallbacks for jobs persisted before the split.
		signals: v.optional(v.array(getOptimusSettingsSignalSchema(params))),
		signals_ml: v.optional(v.array(getOptimusSettingsSignalOptimizationSchema(params))),
		network_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'settings.network_id' }),
		objectives: v.object({
			safety: v.number(),
			bandwidth: v.number(),
			routes_weights: v.array(
				v.object({
					weight: v.number(),
					route_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'settings.objectives.route_id' }),
				}),
			),
			directional_bias: v.object({
				weight: v.optional(v.nullable(v.number())),
				route_id: v.optional(
					v.nullable(
						ValibotCommon.getUuidSchema_V2({
							...params,
							error_field_name: 'settings.objectives.directional_bias.route_id',
						}),
					),
				),
				direction: v.optional(v.nullable(v.picklist(['NB', 'SB', 'EB', 'WB']))),
				weights_range: v.array(v.number()),
				start_signal_id: ValibotCommon.getUuidSchema_V2({
					...params,
					error_field_name: 'settings.objectives.directional_bias.start_signal_id',
				}),
			}),
			target_speed_mph: v.nullable(v.number()),
			safety_weights_range: v.array(v.number()),
			bandwidth_weights_range: v.array(v.number()),
			side_street_control_delay_tolerance: v.number(),
			side_street_control_delay_tolerance_weights_range: v.array(v.number()),
		}),
		// Canonical persisted field (optional only so pre-split historical jobs still validate on read).
		signals_optimization: v.optional(v.array(getOptimusSettingsSignalOptimizationSchema(params))),
		signals_deployed: v.optional(v.array(getOptimusSettingsSignalDeployedSchema(params))),
		// Keyed by signal id. Optional so jobs submitted before this existed still validate on read —
		// the report falls back to split_input when the key is absent.
		overlapping_plans: v.optional(v.record(v.string(), optimus_overlapping_plans_schema)),
		customer_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'settings.customer_id' }),
		timing_parameters: v.object({
			splits: v.boolean(),
			offsets: v.boolean(),
			cycle_lengths: v.boolean(),
			phase_sequence: v.boolean(),
		}),
		reference_time_period: v.object({
			date_end: v.string(),
			date_start: v.string(),
			period_days: v.number(),
			timezone_iana: v.string(),
			excluded_dates: v.array(v.string()),
			exclude_holidays: v.boolean(),
		}),
		cycle_length_constraints: v.object({
			selected_cycle_lengths: v.pipe(v.array(v.string()), v.maxLength(3, 'selected cycle lengths must not exceed 3')),
			allow_half_cycles: v.boolean(),
			match_cycle_lengths: v.boolean(),
		}),
	})
}

// ====================================
// =========== Job Details ============
// ====================================

export const getOptimusV2JobDetailsSchema = (params: GenerateSchemaParams) => {
	return v.object({
		job_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'job_id' }),
		created_at: ValibotCommon.getDateOrIsoStringSchema(),
		local_created_at: ValibotCommon.getDateOrIsoStringSchema(),
		updated_at: ValibotCommon.getDateOrIsoStringSchema(),
		local_updated_at: ValibotCommon.getDateOrIsoStringSchema(),
		deployment_date: v.nullable(ValibotCommon.getDateOrIsoStringSchema()),
		local_deployment_date: v.nullable(ValibotCommon.getDateOrIsoStringSchema()),
		deployed_by: v.nullable(ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'deployed_by' })),
		customer_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'customer_id' }),
		status: job_status_schema,
		requested_by_name: v.string(),
		approved_by_name: v.nullable(v.string()),
		tod_name: v.string(),
		tod_display: v.string(),
		job_name: v.nullable(v.string()),
		network_name: v.string(),
		offset_input: v.array(getOptimusV2OffsetInputSchema(params)),
		split_input: v.optional(v.record(v.string(), getOptimusSplitSignalSchema())),
		network_metrics_input: optimus_network_metrics_schema,
		offset_output: v.nullable(v.array(getOptimusV2OffsetInputSchema(params))),
		split_output: v.nullable(v.record(v.string(), getOptimusSplitSignalSchema())),
		network_metrics_output: v.nullable(optimus_network_metrics_schema),
		parent_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'parent_id' }),
		settings: getOptimusJobSettingsSchema(params),
		version: v.number(),
		is_visible: v.optional(v.boolean()),
		is_remote: v.optional(v.boolean()),
	})
}

// ====================================
// =========== Split Analysis =========
// ====================================

const optimus_edge_connection_map_entry_schema = v.object({
	turn: v.picklist(['through', 'left', 'right']),
	lane_count: v.number(),
	approach_direction: v.optional(v.nullable(v.picklist(['NB', 'SB', 'EB', 'WB']))),
})

export const getOptimusJobSplitAnalysisSchema = () => {
	return v.object({
		edge_connection_map: v.record(v.string(), optimus_edge_connection_map_entry_schema),
	})
}

export const submitOptimusJobBodySchema = (params: GenerateSchemaParams) => {
	return v.object({
		customer_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'customer_id' }),
		network_id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'network_id' }),
		tod: v.object({
			id: v.union([ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'tod.id' }), v.literal('custom')]),
			name: v.string(),
			mode: v.nullable(v.picklist(['coordinated', 'free', 'actuated-free', 'flash', 'actuated'])),
			days_boolean: v.object({
				monday: v.boolean(),
				tuesday: v.boolean(),
				wednesday: v.boolean(),
				thursday: v.boolean(),
				friday: v.boolean(),
				saturday: v.boolean(),
				sunday: v.boolean(),
			}),
			local_start_time: v.string(),
			local_end_time: v.string(),
			days: v.array(v.string()),
			months: v.optional(
				v.object({
					january: v.boolean(),
					february: v.boolean(),
					march: v.boolean(),
					april: v.boolean(),
					may: v.boolean(),
					june: v.boolean(),
					july: v.boolean(),
					august: v.boolean(),
					september: v.boolean(),
					october: v.boolean(),
					november: v.boolean(),
					december: v.boolean(),
				}),
			),
			days_of_month: v.optional(v.record(v.string(), v.boolean())),
		}),
		reference_time_period: v.object({
			exclude_holidays: v.boolean(),
			excluded_dates: v.array(v.string()),
			period_days: v.number(),
			timezone_iana: v.string(),
			date_end: v.string(),
			date_start: v.string(),
		}),
		timing_parameters: v.object({
			offsets: v.boolean(),
			cycle_lengths: v.boolean(),
			splits: v.boolean(),
			phase_sequence: v.boolean(),
		}),
		cycle_length_constraints: v.object({
			selected_cycle_lengths: v.pipe(
				v.array(v.string()),
				v.maxLength(3, 'selected_cycle_lengths length must be 3 or less '),
			),
			match_cycle_lengths: v.boolean(),
			allow_half_cycles: v.boolean(),
		}),
		objectives: v.object({
			directional_bias: v.object({
				weight: v.number(),
				start_signal_id: v.optional(v.string()),
				route_id: v.optional(
					v.nullable(
						ValibotCommon.getUuidSchema_V2({
							...params,
							error_field_name: 'objectives.directional_bias.route_id',
						}),
					),
				),
				direction: v.optional(v.nullable(v.picklist(['NB', 'SB', 'EB', 'WB']))),
			}),
			target_speed_mph: v.nullable(v.number()),
			safety: v.number(),
			bandwidth: v.number(),
			side_street_control_delay_tolerance: v.number(),
		}),
		signals_optimization: v.array(
			v.object({
				is_configured: v.boolean(),
				has_flowlabs_supplemental_params: v.boolean(),
				id: ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'signals_optimization.id' }),
				enable_optimization: v.boolean(),
				split_constraints: v.optional(v.record(v.string(), optimus_split_constraint_schema)),
				cycle_length_constraints: v.optional(optimus_cycle_length_constraints_schema),
				offset_constraints: v.optional(optimus_offset_constraints_schema),
				operating_mode_constraints: v.optional(optimus_operating_mode_constraints_schema),
				enable_split_optimization: v.optional(v.boolean()),
			}),
		),
		job_name: v.string(),
		parent_id: v.optional(ValibotCommon.getUuidSchema_V2({ ...params, error_field_name: 'parent_id' })),
		dry_run: v.optional(v.boolean()),
		is_visible: v.optional(v.boolean()),
		is_remote: v.optional(v.boolean()),
	})
}
